import React from "react";
import footballField from "../../../assets/images/Entity/FootballField.png";
import { colors } from "../../../theme/palette";
import { Box, Skeleton, Typography } from "@mui/material";
import { PlayerPositions } from "../../../enums/entity.enum";
import { useTranslation } from "react-i18next";

const positionStyles: { [key in PlayerPositions]: React.CSSProperties[] } = {
  [PlayerPositions.goalkeeper]: [{ top: "44.21%", left: "2.18%" }],
  [PlayerPositions.leftBack]: [{ top: "7.69%", left: "21.2%" }],
  [PlayerPositions.centerBack]: [
    { top: "25.92%", left: "21.2%" },
    { top: "62.32%", left: "21.2%" },
  ],
  [PlayerPositions.sweeper]: [{ top: "44.21%", left: "17.88%" }],
  [PlayerPositions.rightBack]: [{ top: "80.76%", left: "21.2%" }],
  [PlayerPositions.leftMidfielder]: [{ top: "7.69%", left: "35.75%" }],
  [PlayerPositions.centerMidfielder]: [
    { top: "25.92%", left: "45.24%" },
    { top: "62.32%", left: "45.24%" },
  ],
  [PlayerPositions.defensiveMidFielder]: [{ top: "44.21%", left: "39.12%" }],
  [PlayerPositions.attackingMidFielder]: [{ top: "44.21%", left: "61.99%" }],
  [PlayerPositions.rightMidfielder]: [{ top: "80.76%", left: "35.75%" }],
  [PlayerPositions.leftWinger]: [{ top: "7.69%", left: "61.99%" }],
  [PlayerPositions.rightWinger]: [{ top: "80.76%", left: "61.99%" }],
  [PlayerPositions.striker]: [{ top: "25.92%", left: "79.31%" }],
  [PlayerPositions.centerForward]: [{ top: "62.32%", left: "79.31%" }],
};

const positionShorthands: { [key in PlayerPositions]: string } = {
  [PlayerPositions.goalkeeper]: "GK",
  [PlayerPositions.leftBack]: "LB",
  [PlayerPositions.centerBack]: "CB",
  [PlayerPositions.sweeper]: "SW",
  [PlayerPositions.rightBack]: "RB",
  [PlayerPositions.leftMidfielder]: "LWB",
  [PlayerPositions.centerMidfielder]: "CM",
  [PlayerPositions.defensiveMidFielder]: "DM",
  [PlayerPositions.attackingMidFielder]: "AM",
  [PlayerPositions.rightMidfielder]: "RWB",
  [PlayerPositions.leftWinger]: "LW",
  [PlayerPositions.rightWinger]: "RW",
  [PlayerPositions.striker]: "S",
  [PlayerPositions.centerForward]: "CF",
};

const PlayerPositionLocations = ({
  mainPosition,
  secondaryPosition,
  isLoading,
}: {
  mainPosition?: PlayerPositions;
  secondaryPosition?: PlayerPositions;
  isLoading: boolean;
}) => {
  const highlighted = {
    main: false,
    secondary: false,
  };
  const { t } = useTranslation();

  return (
    <>
      <Box
        sx={{
          width: "100%",
          maxWidth: {
            xs: "100%",
            md: "295px",
          },
        }}
      >
        <Typography
          sx={{
            marginBottom: "9px",
            position: "relative",
            paddingLeft: "16px",
            fontSize: "12px",
            fontWeight: "600",
            colors: colors.Dark800,
            "&::before": {
              content: '""',
              position: "absolute",
              left: "0px",
              top: "50%",
              transform: "translateY(-50%)",
              width: "8px",
              height: "8px",
              backgroundColor: colors.Yellow200,
              borderRadius: "2px",
            },
          }}
        >
          {t("pages.entity.primary")}
        </Typography>
        <Typography
          variant="body16Bold"
          sx={{
            display: "block",
            fontWeight: 700,
            paddingBottom: "20px",
            borderBottom: `1px solid #EAECF0`,
            marginBottom: "20px",
          }}
        >
          {isLoading ? <Skeleton variant="text" width="50%" /> : t(`pages.entity.${mainPosition}`)}
        </Typography>
        <Typography
          variant="subtitle2"
          sx={{
            marginBottom: "9px",
            position: "relative",
            paddingLeft: "16px",
            fontSize: "12px",
            fontWeight: "600",
            colors: colors.Green50,
            "&::before": {
              content: '""',
              position: "absolute",
              left: "0px",
              top: "50%",
              transform: "translateY(-50%)",
              width: "8px",
              height: "8px",
              backgroundColor: colors.green50,
              borderRadius: "2px",
            },
          }}
        >
          {t("pages.entity.other")}
        </Typography>
        <Typography
          variant="body16Bold"
          sx={{
            display: "block",
            fontWeight: 700,
          }}
        >
          {isLoading ? (
            <Skeleton variant="text" width="50%" />
          ) : secondaryPosition ? (
            t(`pages.entity.${secondaryPosition}`)
          ) : (
            "--"
          )}
        </Typography>
      </Box>
      <figure
        style={{
          position: "relative",
          display: "inline-block",
          padding: 0,
          margin: 0,
        }}
      >
        <img
          src={footballField}
          alt="Football Field"
          style={{
            maxWidth: "100%",
          }}
        />
        {Object.entries(positionStyles).map(([position, styles]) => {
          return styles.map((style, index) => {
            const isMain = position === mainPosition && !highlighted.main;
            const isSecondary = position === secondaryPosition && !highlighted.secondary;

            if (isMain) highlighted.main = true;
            if (isSecondary) highlighted.secondary = true;

            return (
              <span
                key={`${position}-${index}`}
                style={{
                  position: "absolute",
                  cursor: "default",
                  ...style,
                  color: isMain || isSecondary ? colors.Dark1000 : colors.White,
                  fontSize: "12px",
                  fontStyle: "normal",
                  fontWeight: 700,
                  lineHeight: "12px",
                  padding: "2px 10px",
                  borderRadius: "4px",
                  border: isMain || isSecondary ? "none" : `1px solid ${colors.Green50}`,
                  background: isMain ? colors.yellow200 : isSecondary ? colors.Green25 : "rgba(255, 255, 255, 0.10)",
                  fontFamily: "Inter",
                }}
              >
                {positionShorthands[position as PlayerPositions]}
              </span>
            );
          });
        })}
      </figure>
    </>
  );
};

export default PlayerPositionLocations;
